import { IonButton, IonFooter, IonSpinner, IonToolbar, useIonToast } from '@ionic/react'
import { ConfirmPatientReportParams } from 'domain/usecases/confirm-patient-report'
import { PatientsService } from 'infra/services/patients-service'
import { ReportsService } from 'infra/services/reports-service'
import { documentText } from 'ionicons/icons'
import { ModalNewPatientValidation } from 'main/validations/usecases/modal-new-patient-validation'
import { Validation } from 'main/validations/usecases/validation'
import { DefaultInput, Message, ModalTitle } from 'presentation/components'
import { useSkipFirstRender } from 'presentation/hooks/skip-first-render'
import React, { useEffect, useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import { modalNewReportAtom, ModalNewReportState, reportPatientAtom } from '../modal-new-report-atom'
import Typeahead, { TypeaheadItem } from 'presentation/components/typeahead/typeahead'
import { UserInfo, UserRole } from 'infra/services/usecases/get-user-info'
import { ClinicsService } from 'infra/services/clinics-service'

interface ConfirmPatientProps {
  patientsService: PatientsService
  reportsService: ReportsService
  userInfo: UserInfo
  validations: ModalNewPatientValidation
  clinicsService: ClinicsService
  dismiss: () => void
  onMergeExisting: () => void
  onConfirmNewPatient: () => void
}

type OncologistTypeaheadItem = {
  label: string
  value: string
  email: string
}

const ConfirmNewPatient: React.FC<ConfirmPatientProps> = (props) => {
  const { reportExtract } = useRecoilValue<ModalNewReportState>(modalNewReportAtom)
  const [confirmPatient, setConfirmPatient] = useRecoilState(reportPatientAtom)
  const [loading, setLoading] = useState(false)
  const [presetToast] = useIonToast()
  const [oncologists, setOncologists] = useState<OncologistTypeaheadItem[]>([])
  const [isLoadingOncologists, setIsLoadingOncologists] = useState(false)

  const handleConfirmPatient = () => {
    setLoading(true)
    props.reportsService
      .confirmNewPatientReport({
        ...confirmPatient,
        oncologistId: confirmPatient.oncologistId || (props.userInfo.id as string),
        reportId: reportExtract?.report_id as string
      })
      .then(() => props.onConfirmNewPatient())
      .catch((e) => {
        showToastError(e.detail[0].msg || 'There was an error trying to confirm patient, try again later.')
      })
      .finally(() => setLoading(false))
  }


  const getOncologists = (search?: string) => {
    setIsLoadingOncologists(true)
    props.clinicsService
      .getOncologists({
        clinic_id: props.userInfo.clinic,
        page: 1,
        size: 10,
        search: search || '',
      })
      .then((result) => {
        const newItems = result.items.map((item) => ({ label: item?.name, value: item?.user_id, email: item?.email }))
        setOncologists(newItems)
      })
      .catch(() => setOncologists([]))
      .finally(() => setIsLoadingOncologists(false))
  }

  const showToastError = (message: string) => {
    presetToast({
      message,
      duration: 5000,
      color: 'danger',
    })
  }

  const isFirstRender = useSkipFirstRender()

  const validate = (fieldName: keyof ConfirmPatientReportParams) => {
    if (!isFirstRender) {
      const updateState = (stateFieldName: keyof ConfirmPatientReportParams, value: string | null) => {
        setConfirmPatient((current) => {
          return {
            ...current,
            [stateFieldName]: value,
          }
        })
      }

      const validateField = (validations: Validation[], value: string) =>
        props.validations.getValidationError(validations, value)

      switch (fieldName) {
        case 'name':
          updateState('nameError', validateField(props.validations.name, confirmPatient.name))
          break
        case 'MRN':
          updateState('mrnError', validateField(props.validations.mrn, confirmPatient.MRN))
          break
        default:
          break
      }
    }
  }

  useEffect(() => {
    if(props.userInfo.role === UserRole.Oncologist) {
      getOncologists(props.userInfo.name)
    }
  }, [props.userInfo.role])

  useEffect(() => {
    setLoading(true)
    const oncologist = oncologists.find((oncologist) => oncologist?.email === props.userInfo.email)
    if(oncologist) {
      setConfirmPatient((s) => ({
        ...s,
        oncologist: oncologist.label as string,
        oncologistId: oncologist.value as string,
      }))
    }
    setLoading(false)
  }, [oncologists])

  useEffect(() => validate('name'), [confirmPatient.name])
  useEffect(() => validate('MRN'), [confirmPatient.MRN])

  return (
    <>
      <section className="ion-padding">
        <ModalTitle label="Confirm New Patient" icon={documentText} />

        <DefaultInput
          name="name"
          state={confirmPatient}
          value={confirmPatient.name}
          setState={setConfirmPatient}
          labelText="Name"
          placeholder="Patient Name"
        />

        {confirmPatient.nameError && <Message color={'error'}>{confirmPatient.nameError}</Message>}

        <DefaultInput
          name="MRN"
          state={confirmPatient}
          value={confirmPatient.MRN}
          setState={setConfirmPatient}
          labelText="Medical Record Number"
          placeholder="MRN"
        />

        {
          props.userInfo.role === UserRole.MedicalAssistant && (
            <Typeahead
              label="Oncologist"
              placeholder="Select an oncologist..."
              options={oncologists}
              isLoading={isLoadingOncologists}
              value={{ label: confirmPatient.oncologist, value: confirmPatient.oncologist }}
              onMenuOpen={() => getOncologists()}
              onTypeaheadDebounce={(search) => getOncologists(search)}
              onChange={(oncologist) =>
                setConfirmPatient((s) => ({
                  ...s,
                  oncologist: (oncologist as TypeaheadItem)?.label as string,
                  oncologistId: (oncologist as TypeaheadItem)?.value as string,
                }))
              }
            />
          )
        }

        {confirmPatient.mrnError && <Message color={'error'}>{confirmPatient.mrnError}</Message>}
      </section>

      <IonFooter>
        <IonToolbar>
          <IonButton slot="end" fill="outline" onClick={props.dismiss}>
            Cancel
          </IonButton>
          <IonButton slot="end" fill="outline" onClick={() => props.onMergeExisting()}>
            Merge to Existing
          </IonButton>
          <IonButton
            slot="end"
            onClick={handleConfirmPatient}
            disabled={
              loading ||
              !!confirmPatient.nameError ||
              !!confirmPatient.mrnError ||
              !confirmPatient.name ||
              !confirmPatient.MRN
            }
          >
            {loading ? <IonSpinner /> : 'Add New Patient'}
          </IonButton>
        </IonToolbar>
      </IonFooter>
    </>
  )
}

export default ConfirmNewPatient
